// src/pages/SystemDetail.js
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { localApplicationsApi as api } from "../utils/api";
import { SystemImageDisplay } from "../components/SystemImage";
import { auditStatus, formatDue, formatDate } from "../utils/dates";
import UserForm   from "../components/UserForm";

const STATUS_BADGE = { ok: "badge-ok", "due-soon": "badge-due", overdue: "badge-overdue", unknown: "badge-neutral" };
const STATUS_LABEL = { ok: "Up to date", "due-soon": "Due soon", overdue: "Overdue", unknown: "Not set" };

const ACTION_LABELS = {
  SYSTEM_CREATED:  { label: "System created",  color: "var(--blue)"   },
  SYSTEM_UPDATED:  { label: "System updated",  color: "var(--gray-500)" },
  SYSTEM_DELETED:  { label: "System deleted",  color: "var(--red)"    },
  USER_ADDED:      { label: "User added",      color: "var(--green)"  },
  USER_UPDATED:    { label: "User updated",    color: "var(--orange)" },
  USER_REMOVED:    { label: "User removed",    color: "var(--red)"    },
  AUDIT_COMPLETED: { label: "Audit completed", color: "var(--green)"  },
  SYNC_COMPLETED:  { label: "Sync completed",  color: "var(--purple)" },
  REMINDER_SENT:   { label: "Reminder sent",   color: "var(--blue)"   },
};


function InlineNoteEditor({ title, icon, value, placeholder, fieldKey, systemId, onSaved }) {
  const [editing,  setEditing]  = React.useState(false);
  const [text,     setText]     = React.useState(value);
  const [saving,   setSaving]   = React.useState(false);
  const [error,    setError]    = React.useState("");

  // Sync if parent reloads
  React.useEffect(() => { setText(value); }, [value]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await api.update(systemId, { [fieldKey]: text });
      setEditing(false);
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleCancel = () => { setText(value); setEditing(false); setError(""); };

  return (
    <div style={{ background: "#FFFBF0", borderRadius: "var(--radius-lg)", padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editing || text ? 8 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14 }}>{icon}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#7A5C00" }}>{title}</span>
          <span className="badge badge-neutral" style={{ fontSize: 10 }}>Admins only</span>
        </div>
        {!editing && (
          <button className="btn-ghost" style={{ fontSize: 12, padding: "3px 8px", color: "#7A5C00" }}
            onClick={() => setEditing(true)}>
            ✏️ Edit
          </button>
        )}
      </div>

      {editing ? (
        <div>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
            placeholder={placeholder}
            style={{ width: "100%", fontSize: 13, resize: "vertical", marginBottom: 8 }} />
          {error && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 6 }}>{error}</div>}
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button className="btn-secondary" style={{ fontSize: 12, padding: "5px 12px" }}
              onClick={handleCancel} disabled={saving}>Cancel</button>
            <button className="btn-primary" style={{ fontSize: 12, padding: "5px 12px" }}
              onClick={handleSave} disabled={saving}>
              {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : "Save"}
            </button>
          </div>
        </div>
      ) : text ? (
        <div style={{ fontSize: 13, color: "#5C4400", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{text}</div>
      ) : (
        <div style={{ fontSize: 12, color: "#B8A060", fontStyle: "italic" }}>{placeholder}</div>
      )}
    </div>
  );
}

export default function SystemDetail({ isAdmin = true }) {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [system,         setSystem]         = useState(null);
  const [auditHistory,   setAuditHistory]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [showUserForm,   setShowUserForm]   = useState(false);
  const [editUser,       setEditUser]       = useState(null);
  const [tab,            setTab]            = useState("users");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(id);
      setSystem(data);
    } catch {
      navigate("/local-applications");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res  = await fetch(`/api/local-applications/${id}/audits`);
      const data = await res.json();
      setAuditHistory(Array.isArray(data) ? data : []);
    } catch {
      setAuditHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);


  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`Remove ${user.displayName}'s access?`)) return;
    await api.removeUser(id, user.id);
    load();
  };

  if (loading) return <div style={{ textAlign: "center", padding: 48 }}><span className="spinner" /></div>;
  if (!system) return null;

  const status      = auditStatus(system.nextAuditDue);
  const activeUsers = (system.users || []).filter(u => u.active);

  return (
    <div>
      <button className="btn-ghost" style={{ marginBottom: 16, paddingLeft: 0, color: "var(--blue)" }} onClick={() => navigate("/local-applications")}>
        ← Back to Local Applications
      </button>

      {/* System header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
              <SystemImageDisplay resource="local-applications" systemId={id} systemName={system.name} size={56} radius={8} cacheKey={system.imageVersion} eager={true} />
              <h1 style={{ fontSize: 20, fontWeight: 600 }}>{system.name}</h1>
              {system.category && <span className="badge badge-info">{system.category}</span>}
              {system.systemType && <span className="badge badge-neutral">{system.systemType}</span>}
              <span className={`badge ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>
            </div>
            {system.description && <p style={{ color: "var(--gray-500)", fontSize: 14, marginBottom: 10 }}>{system.description}</p>}
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {system.server && (
                <div style={{ fontSize: 13 }}>
                  🖥️ {system.server}
                </div>
              )}
              <div style={{ fontSize: 13 }}>
                👤 <strong>Access Owner{(system.accessOwners||[]).length !== 1 ? "s" : ""}:</strong>{" "}
                {(system.accessOwners||[]).length > 0
                  ? system.accessOwners.map(o => o.displayName).join(", ")
                  : <em style={{ color: "var(--gray-400)" }}>Not assigned</em>}
              </div>
              {(system.billingOwners||[]).length > 0 && (
                <div style={{ fontSize: 13 }}>
                  💳 <strong>Billing Owner{system.billingOwners.length !== 1 ? "s" : ""}:</strong>{" "}
                  {system.billingOwners.map(o => o.displayName).join(", ")}
                </div>
              )}
              <div style={{ fontSize: 13 }}>
                🔐 <strong>Auth:</strong> {system.authType || <em style={{ color: "var(--gray-400)" }}>Not set</em>}{system.authNotes && <span style={{ color: "var(--gray-500)" }}> — {system.authNotes}</span>}
              </div>
              <div style={{ fontSize: 13 }}>
                🔁 <strong>Audit cycle:</strong> every {system.auditIntervalMonths} month{system.auditIntervalMonths !== 1 ? "s" : ""}
              </div>
              <div style={{ fontSize: 13 }}>
                📅 <strong>Next audit:</strong> {formatDue(system.nextAuditDue)}
                {system.lastAuditDate && <span style={{ color: "var(--gray-400)", marginLeft: 6 }}>(Last: {formatDate(system.lastAuditDate)})</span>}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {isAdmin && <button className="btn-secondary" onClick={() => navigate(`/local-applications/${id}/edit`)}>Edit</button>}
            <button className="btn-primary"   onClick={() => navigate(`/local-applications/${id}/review`)}>Start Audit Review</button>
          </div>
        </div>
      </div>

      {/* Security & Authentication */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Security & Authentication</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", marginBottom: 3 }}>2FA</div>
            <span className={`badge ${system.twoFactor === "Enforced" ? "badge-ok" : system.twoFactor === "Allowed" ? "badge-info" : system.twoFactor === "Turned Off" ? "badge-overdue" : "badge-neutral"}`}>
              {system.twoFactor || "Unknown"}
            </span>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", marginBottom: 3 }}>Central Management</div>
            <span className={`badge ${system.centralManagement === "Yes" ? "badge-ok" : system.centralManagement === "Partial" ? "badge-due" : "badge-neutral"}`}>
              {system.centralManagement || "No"}
            </span>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", marginBottom: 3 }}>Authentication Type</div>
            <span style={{ fontSize: 13 }}>{system.authType || <em style={{ color: "var(--gray-400)" }}>Not set</em>}</span>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", marginBottom: 3 }}>Password Requirements</div>
            <span style={{ fontSize: 13 }}>{system.passwordRequirements || <span style={{ color: "var(--gray-400)" }}>—</span>}</span>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", marginBottom: 3 }}>Password Expiry</div>
            <span style={{ fontSize: 13 }}>{system.passwordExpiry || <span style={{ color: "var(--gray-400)" }}>—</span>}</span>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", marginBottom: 3 }}>Security Group</div>
            <span style={{ fontSize: 13 }}>{system.securityGroup || <span style={{ color: "var(--gray-400)" }}>—</span>}</span>
          </div>
        </div>
        {system.securityNotes && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--gray-600)" }}>
            <strong style={{ fontSize: 11, color: "var(--gray-500)", display: "block", marginBottom: 3 }}>Security Notes</strong>
            {system.securityNotes}
          </div>
        )}
        {system.authNotes && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--gray-600)", whiteSpace: "pre-wrap" }}>
            <strong style={{ fontSize: 11, color: "var(--gray-500)", display: "block", marginBottom: 3 }}>Authentication Notes</strong>
            {system.authNotes}
          </div>
        )}
      </div>

      {/* Admin Notes — admins only, inline edit */}
      {isAdmin && (
        <div style={{ marginBottom: 16 }}>
          <InlineNoteEditor
            title="Admin Notes"
            icon="🔒"
            value={system.adminNotes || ""}
            placeholder="Internal notes about this system — visible to admins only…"
            fieldKey="adminNotes"
            systemId={id}
            onSaved={load}
          />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20 }}>
        {["users", "history"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: "9px 20px", border: "none", borderRadius: 0, background: "transparent", cursor: "pointer",
              fontWeight: 500, fontSize: 14,
              color:        tab === t ? "var(--blue)" : "var(--gray-500)",
              borderBottom: tab === t ? "2px solid var(--purple)" : "2px solid transparent",
              marginBottom: -1 }}>
            {t === "users" ? `Users (${activeUsers.length})` : `Audit History${auditHistory.length ? ` (${auditHistory.length})` : ""}`}
          </button>
        ))}
      </div>

      {/* Users tab */}
      {tab === "users" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
            <button className="btn-primary" onClick={() => { setEditUser(null); setShowUserForm(true); }}>+ Add User</button>
          </div>
          {activeUsers.length === 0 ? (
            <div className="card empty-state">
              <h3>No users yet</h3>
              <p>Add users to track who has access to {system.name}.</p>
              <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => setShowUserForm(true)}>+ Add User</button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--gray-50)" }}>
                    {["User", "Access Level", "Notes", "Added", ""].map(h => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeUsers.map(user => (
                    <tr key={user.id}>
                      <td style={{ padding: "11px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--blue)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                            {user.displayName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500, fontSize: 13 }}>{user.displayName}</div>
                            <div style={{ fontSize: 12, color: "var(--gray-500)" }}>{user.upn}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "11px 16px" }}>
                        {(Array.isArray(user.accessLevel) ? user.accessLevel : [user.accessLevel]).filter(Boolean).map(l => <span key={l} className="badge badge-info" style={{ marginRight: 3 }}>{l}</span>)}
                      </td>
                      <td style={{ padding: "11px 16px", fontSize: 13, color: "var(--gray-500)" }}>{user.notes || "—"}</td>
                      <td style={{ padding: "11px 16px", fontSize: 12, color: "var(--gray-500)" }}>{formatDate(user.createdAt)}</td>
                      <td style={{ padding: "11px 12px", whiteSpace: "nowrap" }}>
                        <button className="btn-ghost" style={{ fontSize: 13 }} onClick={() => { setEditUser(user); setShowUserForm(true); }}>✏️</button>
                        <button className="btn-ghost" style={{ fontSize: 13, color: "var(--red)" }} onClick={() => handleRemoveUser(user)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* History tab */}
      {tab === "history" && (
        <div>
          {historyLoading ? (
            <div className="card" style={{ textAlign: "center", padding: 48 }}><span className="spinner" /></div>
          ) : auditHistory.length === 0 ? (
            <div className="card empty-state">
              <h3>No audit history yet</h3>
              <p>History is recorded when local applications are created, users are changed, and audits are submitted.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--gray-50)" }}>
                    {["When", "Action", "By", "Detail"].map(h => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditHistory.map(entry => {
                    const meta = ACTION_LABELS[entry.action] || { label: entry.action, color: "var(--gray-500)" };
                    return (
                      <tr key={entry.id}>
                        <td style={{ padding: "10px 16px", fontSize: 12, color: "var(--gray-500)", whiteSpace: "nowrap" }}>
                          {formatDate(entry.timestamp)}
                          <div style={{ fontSize: 11, color: "var(--gray-400)" }}>
                            {new Date(entry.timestamp).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </td>
                        <td style={{ padding: "10px 16px" }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: meta.color }}>{meta.label}</span>
                        </td>
                        <td style={{ padding: "10px 16px", fontSize: 13 }}>
                          {entry.auditedByName || entry.auditedByUpn || "System"}
                        </td>
                        <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--gray-500)", maxWidth: 360 }}>
                          {entry.detail}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showUserForm && (
        <UserForm systemId={id} user={editUser} api={api}
          systemAccessLevels={system?.accessLevels || []}
          onClose={() => { setShowUserForm(false); setEditUser(null); }}
          onSaved={() => { load(); setShowUserForm(false); setEditUser(null); }} />
      )}
    </div>
  );
}
